import type { CalendarEvent, Enduser } from "@tellescope/types-models";

export interface EventStatus {
  isCancelled: boolean;
  isRescheduled: boolean;
  isUpcoming: boolean;
  isPast: boolean;
  canJoinVideo: boolean;
}

/**
 * Hook to determine the status of a calendar event.
 * Ensures consistent event status logic across all components.
 *
 * @param event - The calendar event to check status for
 * @param currentEnduser - Optional current enduser (for group event cancellations)
 * @returns Event status details
 *
 * @example
 * const { isCancelled, isUpcoming, canJoinVideo } = useEventStatus(event, currentEnduser);
 * if (canJoinVideo) {
 *   // Show video join button
 * }
 */
export const useEventStatus = (event: CalendarEvent, currentEnduser?: Enduser | null): EventStatus => {
  const isCancelled = !!event.cancelledAt || !!event.cancelledGroupAttendees?.find((e) => e.id === currentEnduser?.id);

  const isRescheduled = !!event.rescheduledAt;

  const isUpcoming = event.startTimeInMS > Date.now();
  const isPast = event.startTimeInMS <= Date.now();

  // Video can only be joined if upcoming and not cancelled
  const canJoinVideo = isUpcoming && !isCancelled;

  return {
    isCancelled,
    isRescheduled,
    isUpcoming,
    isPast,
    canJoinVideo,
  };
};
